import { useState, useEffect, useCallback, useRef } from 'react';
import { History, AlertCircle, Loader2 } from 'lucide-react';
import { useI18n } from '@/lib/i18n';
import { Pagination } from '@/components/ui/Pagination';
import { safeJsonParse } from './safe-json';
import { LedgerGroupRow } from './LedgerGroupRow';
import { longestGroupSeconds, type LedgerGroup } from './ledger-labels';
import {
  FIRST_PAGE,
  cursorForPage,
  hasNextPage,
  recordPage,
  type LedgerCursor,
  type PageStarts,
} from './ledger-pages';

const PAGE_SIZE = 20;

interface LedgerResponse { groups: LedgerGroup[]; nextCursor: LedgerCursor | null; }

export function LedgerCard({ kind, titleKey = 'dashboard.wallet.ledger.title', bare = false }: {
  /** Which half of the ledger to show. Absent means both, which is what the
   *  card did before the Billing/Usage split and what a future caller wanting
   *  one combined list would still get. */
  kind?: 'usage' | 'billing';
  titleKey?: string;
  /** Body only, for a card that supplies its own header (the billing page's
   *  records/credits tabs). Fetching, paging and errors are unchanged. */
  bare?: boolean;
} = {}) {
  const { t } = useI18n();
  const [groups, setGroups] = useState<LedgerGroup[]>([]);
  const [page, setPage] = useState(1);
  const [starts, setStarts] = useState<PageStarts>(FIRST_PAGE);
  const [loading, setLoading] = useState(true);
  /** The page whose fetch last failed, or null. Retry asks for it again. */
  const [failedPage, setFailedPage] = useState<number | null>(null);
  const started = useRef(false);
  const inFlight = useRef(false);
  const cardRef = useRef<HTMLDivElement>(null);

  /** Fetches `target` and shows it. Resolves true once it is on screen. */
  const load = useCallback(async (target: number, from: LedgerCursor | null): Promise<boolean> => {
    // The concurrency guard lives HERE, not only on the pager. Two overlapping
    // fetches can resolve out of order, and the slower one would put one page's
    // rows under the other page's number — in a billing ledger, a user reading
    // charges under the wrong page. A `disabled` prop guards one control; this
    // guards every caller, including the next one somebody adds.
    if (inFlight.current) return false;
    inFlight.current = true;
    setLoading(true);
    setFailedPage(null);
    try {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
      if (kind) params.set('kind', kind);
      if (from) {
        params.set('beforeAt', String(from.at));
        params.set('beforeGroup', from.groupId);
      }
      const response = await fetch(`/api/wallet/ledger?${params}`, { credentials: 'include' });
      if (!response.ok) throw new Error(`ledger ${response.status}`);
      const data = await safeJsonParse<LedgerResponse>(response);
      if (!data) throw new Error('ledger payload was not JSON');
      // Replace, not append: this is a page, not a continuation of the last one.
      setGroups(data.groups);
      setStarts((prev) => recordPage(prev, target, data.nextCursor));
      setPage(target);
      return true;
    } catch (err) {
      console.error('Error fetching wallet ledger:', err);
      // A failure must NOT fall through to the empty state. The card this
      // replaces swallowed its error and rendered "no history", telling a user
      // who had topped up that they never had.
      setFailedPage(target);
      return false;
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  }, [kind]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    load(1, null);
  }, [load]);

  const goTo = async (target: number) => {
    const from = cursorForPage(starts, target);
    if (from === undefined) return;
    if (!(await load(target, from))) return;
    // The pager sits under the rows, so the new page would otherwise open at
    // its bottom. Only ever scroll up: a card whose top is already in view
    // stays where it is. `scroll-margin-top` clears the mobile header.
    const card = cardRef.current;
    if (!card) return;
    const margin = parseFloat(getComputedStyle(card).scrollMarginTop) || 0;
    if (card.getBoundingClientRect().top < margin) card.scrollIntoView({ block: 'start' });
  };

  // The bar scale is the longest session ON THIS PAGE, so turning the page can
  // rescale every bar. That is the intent: the bars compare the rows a reader
  // can see side by side, not this account's whole history.
  const longestSeconds = longestGroupSeconds(groups);
  const showStreams = longestSeconds > 0;
  const hasNext = hasNextPage(starts, page);

  const body = (
      // In a bare card the body is what the pager scrolls back to.
      <div className="wallet-card__content" ref={bare ? cardRef : undefined}>
        {/* `failedPage` alone must NOT decide this. A failure with a page on
            screen is a failure to TURN the page — hiding the rows the user can
            see, and has already read, behind "could not load transactions"
            replaces correct information with a false statement about their own
            money. Only a failure with nothing on screen takes the whole card.

            One retry handler serves both: `failedPage` is exactly the page that
            failed, and its cursor is still in `starts`, which only changes on
            success. */}
        {failedPage !== null && groups.length === 0 ? (
          <div className="wallet-card__unavailable">
            <AlertCircle size={24} />
            <p>{t('dashboard.wallet.ledger.error')}</p>
            <button
              type="button"
              className="wallet-card__submit-btn"
              disabled={loading}
              onClick={() => goTo(failedPage)}
            >
              {t('dashboard.wallet.ledger.retry')}
            </button>
          </div>
        ) : groups.length === 0 && loading ? (
          <div className="wallet-card__loading"><Loader2 className="animate-spin" size={20} /></div>
        ) : groups.length === 0 ? (
          <div className="wallet-card__empty"><p>{t('dashboard.wallet.ledger.empty')}</p></div>
        ) : (
          <>
            <div
              className={`wallet-card__ledger-list${loading ? ' wallet-card__ledger-list--loading' : ''}`}
              aria-busy={loading}
            >
              {groups.map((g) => (
                <LedgerGroupRow key={g.groupId} group={g} longestSeconds={longestSeconds} />
              ))}
            </div>
            {failedPage !== null && (
              // The page on screen stayed. Only the one asked for is missing,
              // and the retry asks for that same page again.
              <div className="wallet-card__ledger-more-error">
                <AlertCircle size={16} />
                <span>{t('dashboard.wallet.ledger.error')}</span>
                <button
                  type="button"
                  className="wallet-card__submit-btn"
                  disabled={loading}
                  onClick={() => goTo(failedPage)}
                >
                  {t('dashboard.wallet.ledger.retry')}
                </button>
              </div>
            )}
            {/* No pager for a history that fits on one page: two disabled
                arrows and "Page 1" would say nothing. */}
            {(page > 1 || hasNext) && (
              <Pagination
                mode="hasMore"
                className="wallet-card__pager"
                page={page}
                pageSize={PAGE_SIZE}
                hasMore={hasNext}
                onPageChange={goTo}
                disabled={loading}
                labels={{
                  prev: t('dashboard.wallet.ledger.prevPage'),
                  next: t('dashboard.wallet.ledger.nextPage'),
                  prevAria: t('dashboard.wallet.ledger.prevPageAria'),
                  nextAria: t('dashboard.wallet.ledger.nextPageAria'),
                  indicator: (p) => t('dashboard.wallet.ledger.page').replace('{page}', String(p)),
                }}
              />
            )}
          </>
        )}
      </div>
  );

  if (bare) return body;

  return (
    <div className="wallet-card wallet-card--full" ref={cardRef}>
      <div className="wallet-card__header">
        <History size={20} />
        <h2>{t(titleKey)}</h2>
        {/* Once, in the header — a key repeated on every row would cost more
            space than the bars it explains. Only where bars are drawn. */}
        {showStreams && (
          <span className="wallet-card__stream-legend">
            <span><i className="wallet-card__stream wallet-card__stream--me" />{t('dashboard.wallet.ledger.mode.me')}</span>
            <span><i className="wallet-card__stream wallet-card__stream--other" />{t('dashboard.wallet.ledger.mode.other')}</span>
          </span>
        )}
      </div>
      {body}
    </div>
  );
}
